import { useEffect, useMemo, useRef, useState } from 'react';
import { useServiceCategoryStore } from '../stores/serviceCategoryStore';
import { useTranslation } from 'react-i18next';
import { ServiceCategory } from '../types/service';
import { resolveServiceCategoryLabel } from '../utils/serviceCategoryLabel';

export function useServiceCategorySelector(profileServices: ServiceCategory[] = []) {
  const { t, i18n } = useTranslation('common');
  const { loading, serviceCategories, hasSettledServiceCategoriesLoad, fetchServiceCategories } =
    useServiceCategoryStore();

  const serviceCategoriesById = new Map<number, ServiceCategory>();
  serviceCategories.forEach(serviceCategory => {
    serviceCategoriesById.set(serviceCategory.id, serviceCategory);
  });
  profileServices.forEach(serviceCategory => {
    if (!serviceCategoriesById.has(serviceCategory.id)) {
      serviceCategoriesById.set(serviceCategory.id, serviceCategory);
    }
  });

  const options = Array.from(serviceCategoriesById.values())
    .map(serviceCategory => ({
      id: serviceCategory.id,
      label: resolveServiceCategoryLabel(serviceCategory, i18n.language, t),
      slug: serviceCategory.slug,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [selectedOptions, setSelectedOptions] = useState<ServiceCategory[]>([]);
  const [searchInput, setSearchInput] = useState('');

  // Once the initial profileServices selection has been applied, stop
  // re-applying it. Without this guard, clearing the selection to pick new
  // service categories (selectedOptions.length hits 0) re-triggers the effect
  // below, which immediately restores the original services from
  // profileServices, making it impossible to change a pre-filled selection.
  const hasHydratedRef = useRef(selectedOptions.length > 0);

  useEffect(() => {
    if (!serviceCategories.length) {
      fetchServiceCategories();
    }
  }, [serviceCategories.length, fetchServiceCategories]);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!selectedOptions.length) {
      const initialSelectedOptions = options.filter(opt =>
        profileServices?.some((s: ServiceCategory) => s.id === opt.id)
      );
      if (initialSelectedOptions.length) {
        setSelectedOptions(initialSelectedOptions);
        hasHydratedRef.current = true;
      }
    }
  }, [profileServices, serviceCategories, selectedOptions.length, options]);

  // Client-side search filtering
  const filteredOptions = useMemo(() => {
    if (!searchInput.trim()) {
      return options;
    }

    const lowerSearchInput = searchInput.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(lowerSearchInput));
  }, [options, searchInput]);

  return {
    options,
    filteredOptions,
    selectedOptions,
    setSelectedOptions,
    searchInput,
    setSearchInput,
    loading,
    hasSettledOptionsLoad: hasSettledServiceCategoriesLoad || serviceCategories.length > 0,
  };
}
